import axios, { AxiosResponse } from 'axios'
import Vue from 'vue'
import store from './store'

export default function interceptor(context: Vue) {
  const vm = context
  const statusCode = Object.freeze({
    Unauthorized: 401,
    BadRequest: 400,
    OK: 200,
    NoContent: 204,
    LoginTimeout: 440,
    UnderMaintenance: 503,
    TooManyRequests: 429,
    InternalServerError: 500
  })

  // Keep overlay visible until ALL in-flight requests finish
  let pendingRequests = 0
  let hideLoadingTimer: ReturnType<typeof setTimeout> | null = null

  const showLoading = () => {
    if (hideLoadingTimer) {
      clearTimeout(hideLoadingTimer)
      hideLoadingTimer = null
    }
    store.dispatch('app/addLoading', true)
  }

  const scheduleHideLoading = () => {
    if (hideLoadingTimer) {
      clearTimeout(hideLoadingTimer)
    }
    hideLoadingTimer = setTimeout(() => {
      if (pendingRequests === 0) {
        store.dispatch('app/addLoading', false)
      }
      hideLoadingTimer = null
    }, 300)
  }

  const onRequestStart = (config: any) => {
    // Allow callers to opt out: config.headers['X-Skip-Loading'] = '1'
    if (config?.headers?.['X-Skip-Loading'] === '1') {
      return config
    }
    pendingRequests += 1
    showLoading()
    return config
  }

  const onRequestEnd = (config?: any) => {
    if (config?.headers?.['X-Skip-Loading'] === '1') {
      return
    }
    pendingRequests = Math.max(0, pendingRequests - 1)
    if (pendingRequests === 0) {
      scheduleHideLoading()
    }
  }

  axios.interceptors.request.use(
    (config: any) => onRequestStart(config),
    (error: any) => {
      onRequestEnd(error?.config)
      return Promise.reject(error)
    }
  )

  axios.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token')
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  const showToast = (msj: string, title: string, variant: string) => {
    vm.$bvToast.toast(msj, {
      title: title,
      variant: variant,
      solid: true,
      toaster: 'b-toaster-bottom-right',
      autoHideDelay: 6000,
      appendToast: true
    })
  }
  const successHandler = (response: AxiosResponse) => {
    switch (response.status) {
      case statusCode.NoContent:
        showToast('Action submitted successfully', 'Success!', 'success')
        break
      case statusCode.OK:
        if (Array.isArray(response.data))
          response.data.forEach(element => {
            if (element.error) {
              showToast(element.error, 'Error!', 'danger')
            } else if (element.result === 1 && !element.error) {
              showToast('Action submitted successfully', 'Success!', 'success')
            } else if (
              element.errorMessage?.includes('success') &&
              element.errorMessage != ' '
            ) {
              showToast(element.errorMessage, 'Success!', 'success')
            } else if (
              element.errorMessage?.includes('does not exist') &&
              element.errorMessage != ' '
            ) {
              showToast(element.errorMessage, 'Error!', 'danger')
            } else if (
              element.errorMessage?.includes('already exist') &&
              element.errorMessage != ' '
            ) {
              showToast(element.errorMessage, 'Warning!', 'warning')
            } else if (
              element.errorMessage?.includes('already') &&
              element.errorMessage != ' '
            ) {
              showToast(element.errorMessage, 'Error!', 'danger')
            }
          })
        break
    }

    onRequestEnd(response.config)
    return response
  }
  const errorHandler = async (error: any) => {
    onRequestEnd(error?.config)

    if (!error.response) return Promise.reject(error)

    switch (error.response.status) {
      case statusCode.BadRequest:
        {
          const payload = error.response.data
          const messages: string[] = []
          if (typeof payload === 'string' && payload.trim()) {
            messages.push(payload)
          } else if (Array.isArray(payload?.errors)) {
            messages.push(
              ...payload.errors.filter(
                (err: unknown) => typeof err === 'string'
              )
            )
          } else if (payload?.errors && typeof payload.errors === 'object') {
            Object.values(payload.errors).forEach((value: unknown) => {
              if (Array.isArray(value)) {
                value.forEach(err => {
                  if (typeof err === 'string') messages.push(err)
                })
              } else if (typeof value === 'string') {
                messages.push(value)
              }
            })
          } else if (
            typeof payload?.title === 'string' &&
            payload.title.trim()
          ) {
            messages.push(payload.title)
          }
          messages.forEach((err: string) => {
            if (
              err.includes(
                'Optimistic concurrency failure, object has been modified.'
              )
            ) {
              return
            }
            if (err != ' ' && !err.includes('success')) {
              showToast(err, 'Error!', 'danger')
            }
            if (err.includes('success')) {
              context.$root.$emit('hideModal')
              showToast(`${err}`, 'Success!', 'success')
            }
          })
        }
        if (
          error.response.data.requiresPwdChange &&
          !(error.config?.url || '').includes('GoogleSignIn')
        ) {
          let username = ''
          try {
            const body = JSON.parse(error.config?.data || '{}')
            username = body.userName || body.username || ''
          } catch (e) {
            username = ''
          }
          vm.$router.push({
            name: 'OtpLoginPage',
            query: username ? { username } : {}
          })
        }
        // error.response.data.requiresPwdChange.forEach((err: string) => {
        //   if (err === 'True') {
        //     vm.$router.push({ name: 'OtpLoginPage' })
        //   }
        // })

        break
      case statusCode.Unauthorized:
        vm.$router.push({ name: 'Login' })
        break
      case statusCode.UnderMaintenance:
        vm.$router.push({ name: 'UnderMaintenance' })
        break
      case statusCode.TooManyRequests:
        showToast(
          'Too many requests. Please wait a moment and try again.',
          'Too Many Requests',
          'warning'
        )
        break
      case statusCode.InternalServerError:
        {
          const payload = error.response.data
          const messages: string[] = []
          if (Array.isArray(payload?.errors)) {
            messages.push(
              ...payload.errors.filter(
                (err: unknown) => typeof err === 'string'
              )
            )
          } else if (
            typeof payload?.title === 'string' &&
            payload.title.trim()
          ) {
            messages.push(payload.title)
          }
          if (messages.length === 0) {
            messages.push('Something went wrong. Please try again.')
          }
          messages.forEach((err: string) => {
            showToast(err, 'Error!', 'danger')
          })
        }
        break
    }
    // const originalRequest = error.config;
    // switch (error.response?.status) {
    //   case statusCode.Unauthorized:
    //     if(JSON.parse(error.response?.headers["token-expired"]) && !originalRequest._retry) {
    //       originalRequest._retry = true;
    //       const _userInfo = await store.dispatch("auth/refreshToken", { token: localStorage.getItem("token"), tokenKey: localStorage.getItem("tokenKey") });
    //       error.config.headers.Authorization = 'Bearer ' + _userInfo.token;

    //       return axios(originalRequest);
    //     } else {
    //       await store.dispatch("auth/logOut");
    //       vm.$router.push({ name: "Login" });
    //       showToast('Usted no tiene permiso para ingresar.', '¡Prohibido!', 'danger');
    //     }

    //     break;

    //   case statusCode.BadRequest:
    //     error.response.data.errors.forEach((err: string) => {
    //       showToast(err, '¡Error!', 'danger')
    //     });

    //     break;
    //     case statusCode.LoginTimeout:
    //       store.dispatch("auth/addLogout");
    //       vm.$router.push({ name: "Login" });
    //       showToast('Sesión vencida.', 'Error!', 'danger');

    //       break;
    // }

    return Promise.reject(error)
  }

  axios.interceptors.response.use(
    (response: any) => successHandler(response),
    (error: any) => errorHandler(error)
  )
}
